import Immutable from 'immutable';
import {createSelector} from 'reselect';
import {expandTimeRanges} from 'store/search/helpers';
import {getElementPath, getTokenPathByTime, setInEach, findIndexByTime} from './helpers';

const getElementList = (state) => state.elementList;
const getEntities = (state) => state.elementList.get('entities');
const getEntityTypes = (state) => state.elementList.get('entityTypes');
const getVideoDuration = (state) => state.video.get('duration');
const getCurrentTime = (state) => state.video.get('currentTime');
const getSearchResults = (state) => state.search.get('results');
const getActiveSearchResultIndex = (state) => state.search.get('resultIndex');
const getWidth = (state) => state.ui.get('width');

export const highlightSearchResults = createSelector(
    [getElementList, getSearchResults, getActiveSearchResultIndex],
    (elementList, results, activeResultIndex) => {
        const activeResult = results.get(activeResultIndex);

        // Highlight tokens
        results.forEach((result, resultIndex) => {
            const tokens = result.get('tokens', []);
            tokens.forEach((token, tokenIndex) => {
                let range = token.get('range');
                // Highlight in between spaces
                if (tokenIndex < tokens.size - 1) range = range.update(1, value => value + 1);
                elementList = elementList.mergeDeepIn(getElementPath(token.get('path')), {
                    highlight: {
                        result: true,
                        activeResult: resultIndex === activeResultIndex,
                        range,
                    },
                });
            });
        });

        // Highlight speakers
        const speakerIds = [];
        results.forEach(result => {
            if (result.get('type') === 'Speaker') {
                const speakerId = result.get('id');
                if (!speakerIds.includes(speakerId)) speakerIds.push(speakerId);
            }
        });

        elementList.get('segments').forEach((segment, segmentIndex) => {
            const speakerId = segment.getIn(['speaker', 'id']);
            if (speakerIds.includes(speakerId)) {
                const path = ['segments', segmentIndex, 'speaker'];
                elementList = elementList.mergeDeepIn(path, {
                    highlight: {
                        result: true,
                        activeResult: segment.get('startTime') === activeResult.get('startTime'),
                    },
                });
            }
        });

        return elementList;
    },
);

// let aux = 0.0;

// TODO: refactor this selector for usage without highlightSearchResults
export const highlightCurrentToken = createSelector(
    [highlightSearchResults, getCurrentTime],
    (elementList, currentTime) => {

        const currentTimeTokenPath = getTokenPathByTime(elementList, currentTime);
        // const token = elementList.getIn(['segments', currentTimeTokenPath[0], 'sequences', currentTimeTokenPath[1], 'tokens', currentTimeTokenPath[2]]);
        // console.log(`Selected token: '${token.get('displayAs')}' (${token.get('startTime')} - ${token.get('endTime')})`);
        // console.log(`Delta: ${currentTime - aux}`);
        // aux = currentTime;
        return setInEach(elementList, currentTimeTokenPath, ['highlight', 'time'], true);
    },
);

// This selector should always be the last one in the chain
// so we don't have to update connect() calls in components
export const getElementListForTranscript = createSelector(
    [highlightCurrentToken],
    (elementList) => (elementList),
);

export const maxCharsForCaptionLine = 42;

const splitSegment = (segment) => {
    const output = [];
    let currentCount = 0;
    let currentSegment;
    segment.get('sequences').forEach((sequence, sequenceIndex) => {
        // Calculate the sequence length (word length + punctuation)
        const tokens = sequence.get('tokens');
        let sequenceLength = tokens.map(token => token.get('displayAs').length).reduce((r, n) => r + n, 0);

        // Add 1 for the blank space
        if (sequenceIndex < segment.get('sequences').size - 1) sequenceLength++;

        // Should we create a new segment?
        if (currentCount + sequenceLength > maxCharsForCaptionLine || !currentSegment) {
            if (currentSegment) output.push(currentSegment);

            currentCount = 0;
            currentSegment = {
                path: segment.get('path'),
                speaker: segment.get('speaker'),
                speakerChange: sequenceIndex === 0 && segment.get('speakerChange'),
                startTime: sequence.get('startTime'),
                sequences: [],
            };
        }

        // Update the current segment
        currentCount += sequenceLength;
        currentSegment.sequences.push(sequence);
        currentSegment.endTime = sequence.get('endTime');

        // Make sure to include the incomplete segment at the end
        if (sequenceIndex === segment.get('sequences').size - 1) output.push(currentSegment);
    });
    return Immutable.fromJS(output);
};

export const getSegmentsForCaptions = createSelector(
    [getElementList, getCurrentTime],
    (elementList, currentTime) => {
        const currentTimeTokenPath = getTokenPathByTime(elementList, currentTime);
        elementList = setInEach(elementList, currentTimeTokenPath, ['highlight', 'time'], true);

        const segmentPath = getElementPath([currentTimeTokenPath[0]]);
        const fullSegment = elementList.getIn(segmentPath);
        const segments = splitSegment(fullSegment);

        // Only show 2 lines at a time
        const activeIndex = findIndexByTime(segments, currentTime);
        const startIndex = Math.max(0, activeIndex % 2 === 0 ? activeIndex : activeIndex - 1);
        const endIndex = Math.min(segments.size - 1, activeIndex % 2 === 0 ? activeIndex + 1 : activeIndex);
        const visibleSegments = segments.slice(startIndex, endIndex + 1);

        return visibleSegments;
    },
);

export const getEntitiesGroupedByType = createSelector(
    [getEntities, getEntityTypes],
    (allEntities, entityTypes) => (
        entityTypes.map(entityType => {
            const entities = allEntities
                .filter(entity => entity.get('type') === entityType.get('value'))
                .map((entity, index) => entity.merge({index}))
                .sort((a, b) => b.get('timeRanges').size - a.get('timeRanges').size);
            return entityType.merge({entities});
        })
    ),
);

export const blockWidth = 30;

export const getHeatmapBlocks = createSelector(
    [getVideoDuration, getEntities, getSearchResults, getWidth],
    (videoDuration, entities, results, rootWidth) => {
        if (rootWidth === 0 || videoDuration === 0) return Immutable.List();

        const numBlocks = Math.floor(rootWidth / exports.blockWidth);
        const blockStep = videoDuration / numBlocks;
        const blocks = Array.from(new Array(numBlocks), () => []);

        const updateBlocks = (entity) => {
            const startBlock = Math.max(0, Math.min(numBlocks - 1, Math.floor(entity.get('startTime') / blockStep)));
            const endBlock = Math.max(0, Math.min(numBlocks - 1, Math.floor(entity.get('endTime') / blockStep)));
            Immutable.Range(startBlock, endBlock + 1).forEach(blockIndex => {
                blocks[blockIndex].push(entity);
            });
        };

        if (results.size > 0) results.forEach(updateBlocks);
        else entities.flatMap(expandTimeRanges).forEach(updateBlocks);

        if (blocks.some(blockEntities => blockEntities.length)) {
            let max = 1;
            blocks.forEach(blockEntities => max = Math.max(max, blockEntities.length));

            return Immutable.fromJS(blocks.map((blockEntities, index) => {
                const weight = blockEntities.length / max;
                const startTime = blockStep * index;
                const endTime = startTime + blockStep;

                const groupedEntities = Immutable.fromJS(blockEntities)
                    .groupBy(entity => entity.get('value'))
                    .map(group => Immutable.Map({
                        count: group.size,
                        value: group.getIn([0, 'value']),
                        displayAs: group.getIn([0, 'displayAs']),
                        confidence: group.getIn([0, 'confidence']),
                        types: group.map(entity => entity.get('type')).toSet().toList(),
                    }))
                    .toList()
                    .sortBy(entity => entity.get('confidence') * -1);

                return {
                    entities: groupedEntities,
                    weight,
                    startTime,
                    endTime,
                    index,
                    numBlocks,
                };
            }));
        }

        return Immutable.List();
    },
);



// WEBPACK FOOTER //
// ./scripts/store/element-list/selectors.js